/**
 * Slice 55 — Test clean. The owner runs a real clean on their own phone as the cleaner.
 * It is a real job flagged is_test: it shows in Reports tagged "Test clean", flags counts and
 * missing / broken items like a real clean, and never counts in stats or billing.
 */
import { addDays, format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export const TEST_CHECKOUT_TIMES = ["10:00", "11:00", "12:00", "13:00"] as const;

export interface TestCleanSetup {
  guests: number;
  guestLeft: boolean;
  sameDay: boolean;
  extraDirty: boolean;
  checkOutTime: string;
}

export const DEFAULT_TEST_SETUP: TestCleanSetup = {
  guests: 4,
  guestLeft: true,
  sameDay: false,
  extraDirty: false,
  checkOutTime: "11:00",
};

const FIRST_TIME_KEY = "stravo:testCleanNoteSeen";

export function hasSeenTestCleanNote(): boolean {
  try {
    return localStorage.getItem(FIRST_TIME_KEY) === "1";
  } catch {
    return false;
  }
}

export function markTestCleanNoteSeen() {
  try {
    localStorage.setItem(FIRST_TIME_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** "4 guests · guest had left · same-day check-in · check-out 11:00 · extra dirty" */
export function testSetupLine(raw: unknown): string | null {
  const s = raw as Partial<{
    guests: number;
    guest_left: boolean;
    same_day: boolean;
    extra_dirty: boolean;
    check_out_time: string;
  }> | null;
  if (!s || typeof s !== "object" || s.guests == null) return null;
  return [
    `${s.guests} guest${s.guests === 1 ? "" : "s"}`,
    s.guest_left ? "guest had left" : "guest still inside",
    s.same_day ? "same-day check-in" : "no same-day check-in",
    `check-out ${s.check_out_time ?? "11:00"}`,
    s.extra_dirty ? "extra dirty" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Create the pretend turnover: a departing test reservation (guests, check-out time, whether the guest
 * has left), an optional next-guest reservation for same-day check-in, and a job assigned to the owner.
 */
export async function createTestClean(args: {
  propertyId: string;
  userId: string;
  setup: TestCleanSetup;
}): Promise<{ taskId: string }> {
  const { propertyId, userId, setup } = args;
  const today = format(new Date(), "yyyy-MM-dd");
  const yesterday = format(addDays(new Date(), -1), "yyyy-MM-dd");
  const tomorrow = format(addDays(new Date(), 1), "yyyy-MM-dd");

  const { data: property, error: propErr } = await supabase
    .from("properties")
    .select("owner_id, default_checklist_id")
    .eq("id", propertyId)
    .maybeSingle();
  if (propErr) throw propErr;
  const isOwner = (property as { owner_id?: string } | null)?.owner_id === userId;
  const checklistId = (property as { default_checklist_id?: string | null } | null)?.default_checklist_id ?? null;

  const reservationIds: string[] = [];
  const { data: departing, error: resErr } = await supabase
    .from("reservations")
    .insert({
      property_id: propertyId,
      start_date: yesterday,
      end_date: today,
      source: "Test",
      summary: "Test clean",
      is_test: true,
      guest_count: setup.guests,
      check_out_time: setup.checkOutTime,
      early_checkout_detected: setup.guestLeft,
    } as never)
    .select()
    .single();
  if (resErr) throw resErr;
  reservationIds.push((departing as { id: string }).id);

  if (setup.sameDay) {
    const { data: next, error: nextErr } = await supabase
      .from("reservations")
      .insert({
        property_id: propertyId,
        start_date: today,
        end_date: tomorrow,
        source: "Test",
        summary: "Test clean · next guest",
        is_test: true,
        check_in_time: "15:00",
      } as never)
      .select()
      .single();
    if (nextErr) throw nextErr;
    reservationIds.push((next as { id: string }).id);
  }

  const { data: task, error: taskErr } = await supabase
    .from("cleaning_tasks")
    .insert({
      property_id: propertyId,
      reservation_id: reservationIds[0],
      checklist_id: checklistId,
      cleaner_id: null,
      assignee_user_id: userId,
      assigned_type: isOwner ? "owner" : "team",
      assignment_type: "manual",
      assignment_reason: "Test clean",
      scheduled_date: today,
      status: "scheduled",
      is_test: true,
      extra_dirty: setup.extraDirty,
      extra_dirty_notes: setup.extraDirty ? "Owner says: extra dirty" : null,
      test_setup: {
        guests: setup.guests,
        guest_left: setup.guestLeft,
        same_day: setup.sameDay,
        extra_dirty: setup.extraDirty,
        check_out_time: setup.checkOutTime,
        reservation_ids: reservationIds,
      },
    } as never)
    .select("id")
    .single();
  if (taskErr) throw taskErr;
  return { taskId: (task as { id: string }).id };
}

export async function deleteTestClean(taskId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_test_clean" as never, { p_task_id: taskId } as never);
  if (error) throw error;
}

/** End early and keep what was done: shows in Reports as "Test clean · ended early". */
export async function endTestCleanKeep(taskId: string, setup: unknown): Promise<void> {
  const nextSetup = { ...((setup as Record<string, unknown>) ?? {}), ended_early: true };
  const { error } = await supabase
    .from("cleaning_tasks")
    .update({ test_setup: nextSetup, status: "completed", completed_at: new Date().toISOString() } as never)
    .eq("id", taskId);
  if (error) throw error;
}

/** Cancel a clean that is still scheduled or in progress: removes the job and what it created, keeps the stay. */
export async function cancelCleaningJob(taskId: string): Promise<void> {
  const { error } = await supabase.rpc("cancel_cleaning_job" as never, { p_task_id: taskId } as never);
  if (error) throw error;
}
